import React, { useState, useEffect } from "react";
import Loader from "../../components/Loader";
import { useNavigate } from "react-router-dom";
import "./Orders.css";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../contexts/AuthProvider";
import Pagination from "../ProductsList/Pagination";
import RenderPaginationNumbers from "../ProductsList/RenderPageNumber";

import { API_URL } from "../../utils/config";
const baseUrl = API_URL;

const Orders = () => {
  const { t } = useTranslation("orders");
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const { isAuthenticated } = useAuth();
  const itemsPerPage = 10;


  // Fixed status array with proper mapping
  const statusOptions = [
    { key: "all", label: "All Orders", apiValue: "all" },
    { key: "pending", label: "Pending", apiValue: "pending" },
    { key: "confirmed", label: "Confirmed", apiValue: "accepted" },
    { key: "paid", label: "Paid", apiValue: "paid" },
    { key: "processing", label: "Processing", apiValue: "processing" },
    { key: "shipped", label: "Shipped", apiValue: "shipped" },
    { key: "noResponded", label: "No Responded", apiValue: "no_responded" },
    { key: "delivered", label: "Delivered", apiValue: "delivered" },
    { key: "rejected", label: "Rejected", apiValue: "rejected" },
    { key: "cancelled", label: "Cancelled", apiValue: "cancelled" }
  ];

  const getLangFromLocalStorage = () => {
    return localStorage.getItem("language") || "en";
  };

  const [statusFilter, setStatusFilter] = useState("all");
  const [loading, setLoading] = useState(true);

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
    const urlParams = new URLSearchParams(window.location.search);
    urlParams.set("page", pageNumber.toString());
    window.history.replaceState(null, "", `?${urlParams.toString()}`);
  };

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const page = parseInt(urlParams.get("page")) || 1;
    setCurrentPage(page);
  }, []);

  const fetchOrders = async () => {
    try {
      if (!isAuthenticated) {
        localStorage.removeItem("user");
        window.location.href = "/";
        return;
      }

      const params = new URLSearchParams({
        limit: itemsPerPage.toString(),
        page: currentPage.toString(),
      });

      const response = await fetch(`${baseUrl}/api/v1/attnd-supplier/suppliers/orders?${params}`, {
        method: "GET",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          lang: getLangFromLocalStorage(),

        },
      });

      if (response.status === 401) {
        navigate("/login");
        throw new Error("Unauthorized access. Please log in again.");
      }

      if (!response.ok) {
        throw new Error("Failed to fetch orders");
      }

      const result = await response.json();
      setTotalPages(result.totalPages || 1);
      setTotalItems(result.totalItems || 0);
      return result.data;
    } catch (error) {
      if (error.response?.status === 401) {
        localStorage.removeItem("token");
        localStorage.removeItem("user");
        navigate("/login");
      }
      return [];
    }
  };

  useEffect(() => {
    const getOrders = async () => {
      setLoading(true);
      const orders = (await fetchOrders()) || [];
      setOrders(orders);
      setLoading(false);
    };
    getOrders();
  }, [currentPage]);

  const transformedOrders = orders.map((order) => ({
    no: order?.id || "Unknown",
    customer: order?.customer || "Unknown",
    email: order?.email || "Unknown",
    cost: order.cost || "Unknown",
    status: order.status || "Unknown",
    customized: order?.customized || false,
  }));

  const filteredOrders = transformedOrders.filter((order) => {
    if (statusFilter === "all") return true;

    // Map the filter value to the actual order status
    const statusOption = statusOptions.find((s) => s.key === statusFilter);
    if (!statusOption) return true;

    return order.status.toLowerCase() === statusOption.apiValue.toLowerCase();
  });

  const handleStatusChange = (statusKey) => {
    setStatusFilter(statusKey);
  };

  const handleOrderDetails = (id) => {
    navigate(`/orders/${id}`);
  };

  const getStatusDisplayName = (status) => {
    const statusLower = status.toLowerCase();

    // Map API status values to translation keys
    const statusTranslationMap = {
      accepted: "pendingPayment",
      pending: "pending",
      paid: "paid",
      processing: "processing",
      shipped: "shipped",
      delivered: "delivered",
      rejected: "rejected",
      no_responded: "noResponded",
      cancelled: "cancelled"
    };

    const translationKey = statusTranslationMap[statusLower];
    if (translationKey) {
      return t(`orders.statuses.${translationKey}`);
    }

    // Fallback for unknown statuses
    return status.charAt(0).toUpperCase() + status.slice(1);
  };

  const getStatusBadgeClass = (status) => {
    const statusLower = status.toLowerCase();

    if (["accepted", "delivered", "paid"].includes(statusLower)) {
      return "confirmed-orders";
    }
    if (statusLower === "rejected" || statusLower === "cancelled") {
      return "rejected";

    }
    if (statusLower === "pending") {
      return "pending-orders";
    }
    if (statusLower === "no_responded") {
      return "pending-orders";
    }
    if (["pending-payment", "processing", "shipped"].includes(statusLower)) {
      return "nostatus-orders";
    }
    return "";
  };

  if (loading) {
    return (
      <div className="loader-container">
        <Loader />
      </div>
    );
  }

  return (
    <>
      <div className="head-addons">
        <h1 className="title-addons">{t("orders.title")}</h1>
        <p className="addons-desc">{t("orders.description")}</p>
        <div className="invitaion-btns">
          {statusOptions.map((statusOption) => (
            <button
              key={statusOption.key}
              className={`orders-btn ${
                statusFilter === statusOption.key ? "active" : ""
              }`}
              onClick={() => handleStatusChange(statusOption.key)}
            >
              {t(`orders.statuses.${statusOption.key}`)}
            </button>
          ))}
        </div>
      </div>
      <div className="orders-container">
        <div className="orders-actions">
          <div className="orders-controls-row">
            <h3 className="invited-heading">
              {statusFilter === "all"
                ? t("orders.statuses.allOrders")
                : t(`orders.statuses.${statusFilter}`)}{" "}
              <span>({filteredOrders.length})</span>
            </h3>
            <div className="controls-actions">
              <div className="status-select">
                <select
                  value={statusFilter}
                  onChange={(e) => handleStatusChange(e.target.value)}
                >
                  {statusOptions.map((statusOption) => (
                    <option key={statusOption.key} value={statusOption.key}>
                      {t(`orders.statuses.${statusOption.key}`)}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        </div>
        <div className="orders-table table-wrapper">
          <table className="orders-table table-none md:table-fixed">
            <thead>
              <tr>
                <th>{t("orders.table.headers.no")}</th>
                <th>{t("orders.table.headers.customer")}</th>
                <th>{t("orders.table.headers.email")}</th>
                <th>{t("orders.table.headers.cost")}</th>
                <th>{t("orders.table.headers.status")}</th>
              </tr>
            </thead>
            <tbody>
              {filteredOrders.length > 0 ? (
                filteredOrders.map((order) => (
                  <tr
                    key={order.no}
                    onClick={() => handleOrderDetails(order.no)}
                  >
                    <td>{order.no}</td>
                    <td>{order.customer}</td>
                    <td>{order.email}</td>
                    <td>
                      {/* if customized is true and total is zero set cost as custom */}
                      {order.customized && order.cost == 0
                        ? t("Custom")
                        : order.cost}
                    </td>
                    <td>
                      <span
                        className={`status-badge ${getStatusBadgeClass(
                          order.status
                        )}`.trim()}
                      >
                        {getStatusDisplayName(order.status)}
                      </span>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="5" className="no-data">
                    {t("orders.table.noData")}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        <div className="orders-mobile-table">
          {filteredOrders.length > 0 ? (
            <ul>
              {filteredOrders.map((order) => (
                <li
                  key={order.no}
                  onClick={() => handleOrderDetails(order.no)}
                  className="orders-card"
                >
                  <div className="orders-card-content">
                    <div className="orders-name">
                      <div>
                        {t("orders.table.headers.no")}: {order.no}
                      </div>
                      <div>
                        {t("orders.table.headers.customer")}: {order.customer}
                      </div>
                      <div>
                        {t("orders.table.headers.email")}: {order.email}
                      </div>
                    </div>
                    <div className="orders-info">
                      {t("orders.table.headers.cost")}: {order.cost}
                    </div>
                  </div>
                  <div className="orders-status">
                    <span
                      className={`status-badge ${getStatusBadgeClass(
                        order.status
                      )}`}
                    >
                      {getStatusDisplayName(order.status)}
                    </span>
                  </div>
                </li>
              ))}
            </ul>
          ) : (
            <div className="no-data">{t("orders.mobile.noData")}</div>
          )}
        </div>

        {/* ------- Pagination -------- */}
        {filteredOrders.length > 0 && totalPages > 1 && (
          <Pagination
            totalPages={totalPages}
            currentPage={currentPage}
            handlePageChange={handlePageChange}
          >
            <RenderPaginationNumbers
              currentPage={currentPage}
              totalPages={totalPages}
              handlePageChange={handlePageChange}
            />
          </Pagination>
        )}
      </div>
    </>
  );
};

export default Orders;
